import { Link } from 'react-router-dom'

const guide = {
  slug: 'how-to-quit-porn',
  category: 'quit',
  kicker: 'Quitting',
  title: 'How to quit porn when willpower keeps losing',
  seoTitle: 'How to Quit Porn When Willpower Keeps Losing',
  description: `You’ve tried to stop and it broke one tired night. Why that’s a mechanism, not a flaw, and the method that removes the nightly fight instead of asking you to win it.`,
  published: '2026-08-25',
  modified: '2026-09-03',
  minutes: 9,
  intro: `You have already tried to stop. You held — three days, two weeks,
          maybe longer. Then one tired evening it started again, and the
          shame came with it. This isn't a piece about being stronger. It's
          about why the fight is rigged, and how to stop fighting it.`,
  next: ['porn-blocker-you-cant-uninstall', 'how-to-block-porn-on-iphone', 'porn-blocker-without-accountability-partner'],
  body: (
    <>
      <h2>Why the promise breaks</h2>
      <p>
        The Sunday-night promise — "that was the last time" — is sincere.
        You mean it. The problem is <em>when</em> it's tested. Willpower is a
        resource that runs down over the day; the urge shows up when it's
        empty. Late, tired, alone, brain on autopilot. The person who made
        the promise in the morning and the person facing the screen at two
        in the morning are not the same person. One of them is fully
        equipped. The other has nothing left to hold on to.
      </p>
      <p>
        And the other side never gets tired. The sites are always open, the
        algorithm is always awake, and the door is always three taps away. A
        contest between a resource that depletes and an option that never
        does has only one outcome. It's not a question of character. It's
        a question of architecture: <b>who decides, and at what hour.</b>
      </p>

      <h2>It's an access problem</h2>
      <p>
        Here's the reframe that changes what you do next: you don't have a
        willpower problem. You have an <b>access</b> problem. Every relapse
        has the same shape — an urge, and a door that opens. Work on the
        urge and you're fighting a feeling that shows up at your weakest
        hour. Work on the door and there is nothing to fight.
      </p>
      <p>
        In front of a locked door there is nothing left to negotiate. That
        sentence is the whole method.
      </p>

      <h2>What doesn't work, and why</h2>
      <ul>
        <li><b>Deleting the apps.</b> They're back in a minute. Reinstalling is free and the store remembers what you had.</li>
        <li><b>A blocker you can turn off.</b> A block you can remove yourself isn't a block; it's a sticky note on the door, and you already know the gesture that takes it off. See <Link to="/guides/porn-blocker-you-cant-uninstall">what makes a blocker immovable</Link>.</li>
        <li><b>Incognito rules, "just not in bed", time limits.</b> Rules you enforce yourself are enforced by the person who's tired.</li>
        <li><b>An accountability partner.</b> It works for some people. For many it adds shame to the loop and a text message to the workaround. If you'd rather not involve anyone, you don't have to — see <Link to="/guides/porn-blocker-without-accountability-partner">quitting without an accountability partner</Link>.</li>
      </ul>

      <h2>What works: a system that holds when you can't</h2>
      <p>
        No coaching, no mental strength required. A technical lock, set
        once on a day you're doing fine, that does the work on the days
        you're not. Four properties make it hold:
      </p>
      <ol>
        <li><b>It's everywhere.</b> Every device you own, every app, every browser, private mode included. If one door stays open, the whole house is open. A DNS-level category filter covers all of them at once.</li>
        <li><b>It can't be undone on impulse.</b> On iPhone and iPad, that means a supervised profile — nothing on the phone can remove it. Taking it off is a deliberate request from your account with a cooling-off delay. Free to stop, never in thirty seconds.</li>
        <li><b>It only tightens.</b> Adding a locked day or a curfew takes effect immediately. Loosening waits twenty-four hours. The floor never drops. An urge to loosen has to survive a night, and it won't.</li>
        <li><b>It closes the night.</b> A curfew — typically 11pm to 7am — cuts the internet entirely in the hours where relapses actually happen. Not porn: the internet. There's nothing to browse toward.</li>
      </ol>

      <h2>Cut it dead, or cut it in steps</h2>
      <p>
        Stopping all at once doesn't work for everyone, and giving yourself
        "limits" without a framework has never worked for anyone. So you
        choose your pace once, at the start, and after that it never goes
        back down:
      </p>
      <ul>
        <li><b>Straight cut.</b> Seven days a week, from the first minute. The hardest path for the first five days, and by far the shortest.</li>
        <li><b>Gradual taper.</b> Four locked days a week at the start, then one more every two weeks. After six weeks you're at seven out of seven — without ever having to decide it on a tired evening.</li>
      </ul>
      <p>
        If you're decided, take the straight cut. If you've already failed
        several times going cold turkey, take the taper. Either way, the
        floor never drops back down.
      </p>

      <h2>What the first weeks are like</h2>
      <p>
        Honestly: in the first days your brain looks for the door and finds
        it locked. That's uncomfortable, and it's also the point — the
        discomfort is the loop failing to complete. The night you want to
        rip it all out and can't is the night the system is working. Users
        describe the pressure easing over the following weeks as the search
        stops finding anything; the day counter does the rest. We don't
        promise a timeline, because we don't invent numbers.
      </p>

      <h2>Then you live</h2>
      <p>
        The part nobody advertises: once the door is shut, there's no daily
        fight left to wage. No app to open every morning, no password to
        remember, nothing to renegotiate at night. The time and energy that
        went into the loop go somewhere else — and that's what makes people
        still here months later, not the filter. SELFCNTRL adds a day
        counter, goals across five areas reset weekly, and a short Sunday
        check-in: no sermon, no ridiculous badge, an honest status report
        and the next step.
      </p>

      <h2>Ten minutes, once</h2>
      <ol>
        <li><Link to="/register">Create your space</Link>. One email address. Nobody but you knows why you're here.</li>
        <li>Lock the phone. Plug it into a computer, run the supervisor, scan a QR code. Ten minutes — the only step that needs a computer. Details in <Link to="/guides/how-to-block-porn-on-iphone">the iPhone guide</Link>; Mac and Windows take a few minutes each.</li>
        <li>Pick your pace and your curfew. Then live.</li>
      </ol>
      <p>
        The one reading this line is the one who decides. Not the you of
        tonight, not the you of the relapse. You, right now, with ten
        minutes ahead of you.
      </p>
    </>
  ),
  faq: [
    {
      q: 'Is a porn blocker enough to quit porn?',
      a: `A blocker you can remove is not. A blocker you can't remove on
          impulse, on every device, with a night curfew, removes the door —
          and without a door there's no relapse to fight. What you do with
          the time you get back is yours; the counter, goals and weekly
          check-in are there for that.`
    },
    {
      q: 'Do I need to tell anyone?',
      a: `No. There's no accountability partner, no report, no shared
          dashboard, and nothing explicit on your bank statement. This stays
          between you and the door.`
    },
    {
      q: 'What if I relapse on a device I haven’t locked yet?',
      a: `Then lock it. A new or unlocked device is untouched until you add
          it — same three moves, same ten minutes. The system only holds
          when there's no door left open.`
    }
  ]
}

export default guide
